import React, { useMemo, useState } from 'react';
import { addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, format, parseISO } from 'date-fns';
import { arSA } from 'date-fns/locale';
import { useAppContext } from '../../hooks/useAppContext';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { API_BASE_URL } from '../../config';
import { Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

type CalendarEvent = {
  id: string;
  employeeId: string;
  title: string;
  description?: string;
  location?: string;
  start: string;
  end?: string | null;
  allDay?: boolean;
  status?: string;
};

const dayNames = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

const CalendarScreen: React.FC = () => {
  const { user, t } = useAppContext();
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);

  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const monthLabel = useMemo(() => format(currentMonth, 'MMMM yyyy', { locale: arSA }), [currentMonth]);

  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
    const days: Date[] = [];
    let d = start;
    while (d <= end) {
      days.push(d);
      d = addDays(d, 1);
    }
    const chunks: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) chunks.push(days.slice(i, i + 7));
    return chunks;
  }, [currentMonth]);

  const loadEvents = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const from = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const to = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      const res = await fetch(`${API_BASE_URL}/calendar-events?from=${from}&to=${to}`, {
        headers: { 'X-User-Id': user.id, 'X-User-Role': user.role }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(data as CalendarEvent[]);
    } catch (e: any) {
      toast.error(e?.message || 'تعذر تحميل التقويم');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { loadEvents(); }, [currentMonth, user?.id]);

  const createEvent = async () => {
    if (!user) return;
    if (!newTitle.trim()) { toast.error('اكتب عنوان الموعد'); return; }
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const startIso = startTime ? new Date(`${dateStr}T${startTime}`).toISOString() : new Date(`${dateStr}T09:00`).toISOString();
      const endIso = endTime ? new Date(`${dateStr}T${endTime}`).toISOString() : null;
      const payload = {
        employeeId: user.id,
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        start: startIso,
        end: endIso,
        allDay: false,
        status: 'SCHEDULED',
      };
      const res = await fetch(`${API_BASE_URL}/calendar-events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': user.id, 'X-User-Role': user.role },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = await res.json();
      setEvents(prev => [created, ...prev]);
      toast.success('تم إضافة الموعد');
      setNewTitle('');
      setNewDescription('');
      setStartTime('');
      setEndTime('');
      setIsCreateOpen(false);
    } catch (e: any) {
      toast.error(e?.message || 'فشل إضافة الموعد');
    }
  };

  const deleteEvent = async (id: string) => {
    if (!user) return;
    try {
      const res = await fetch(`${API_BASE_URL}/calendar-events/${id}`, {
        method: 'DELETE',
        headers: { 'X-User-Id': user.id, 'X-User-Role': user.role }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setEvents(prev => prev.filter(e => e.id !== id));
      toast.success('تم حذف الموعد');
    } catch (e: any) {
      toast.error(e?.message || 'فشل حذف الموعد');
    }
  };

  const eventsForDay = (d: Date) => {
    return events.filter(ev => {
      const s = parseISO(ev.start);
      const e = ev.end ? parseISO(ev.end) : s;
      const inRange = s <= d && e >= d ? true : isSameDay(s, d);
      return inRange;
    });
  };

  return (
    <div className="space-y-6">
      <div className="calendar-container">
        <div className="calendar-header">
          <h2>{monthLabel}</h2>
          <div className="flex gap-2">
            <button className="nav-btn" onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}>السابق</button>
            <button className="nav-btn" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>التالي</button>
          </div>
        </div>
        <div className="calendar-weekdays">
          {dayNames.map(n => (
            <span key={n}>{n}</span>
          ))}
        </div>
        <div className="calendar-days">
          {weeks.map((week, wi) => (
            <React.Fragment key={wi}>
              {week.map(d => {
                const isCurrent = isSameMonth(d, currentMonth);
                const isToday = isSameDay(d, new Date());
                const dayEvents = eventsForDay(d);
                const extra = `${!isCurrent ? ' not-current-month' : ''}${isToday ? ' today' : ''}${dayEvents.length ? ' has-appointment' : ''}`;
                return (
                  <button
                    key={d.toISOString()}
                    onClick={() => { setSelectedDate(d); setStartTime('10:00'); setEndTime(''); setNewTitle(''); setNewDescription(''); setIsCreateOpen(true); }}
                    className={`day${extra}`}
                  >
                    <span className="day-number">{format(d, 'd', { locale: arSA })}</span>
                    <div className="mt-1 space-y-1 overflow-y-auto max-h-16 w-full pr-1">
                      {dayEvents.slice(0, 3).map(ev => (
                        <span key={ev.id} className="appointment-title-small">
                          {format(parseISO(ev.start), 'HH:mm')} - {ev.title}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {isCreateOpen && (
        <div className="modal" style={{ display: 'flex' }} onClick={() => setIsCreateOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <button className="close-btn" onClick={() => setIsCreateOpen(false)}>&times;</button>
              <h3 id="modalTitle">المواعيد لـ <span>{format(selectedDate, 'PPP', { locale: arSA })}</span></h3>
            </div>

            <div className="existing-appointments">
              <h4>المواعيد الحالية:</h4>
              <ul className="appointments-list">
                {eventsForDay(selectedDate).sort((a, b) => (parseISO(a.start).getTime() - parseISO(b.start).getTime())).map(ev => (
                  <li key={ev.id}>
                    <div className="appointment-details">
                      <strong>{format(parseISO(ev.start), 'HH:mm')} - {ev.title}</strong>
                      <span style={{ fontSize: '0.9em', color: '#b0b0b0' }}>{ev.description || 'لا توجد ملاحظات.'}</span>
                    </div>
                    <Button variant="ghost" size="sm" className="p-1 h-auto text-destructive" aria-label="حذف الموعد" onClick={() => deleteEvent(ev.id)}>
                      <Trash2 size={16} />
                    </Button>
                  </li>
                ))}
                {eventsForDay(selectedDate).length === 0 && (
                  <li className="no-appointments">لا توجد مواعيد لهذا اليوم.</li>
                )}
              </ul>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); createEvent(); }} style={{ borderTop: '1px dashed #30363D', paddingTop: 20, marginTop: 20 }}>
              <h4>إضافة موعد جديد:</h4>
              <input type="text" placeholder="عنوان الموعد (مطلوب)" value={newTitle} onChange={e => setNewTitle(e.target.value)} required />
              <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} required />
              <textarea placeholder="ملاحظات الموعد (اختياري)" value={newDescription} onChange={e => setNewDescription(e.target.value)} />
              <button type="submit" id="saveAppointmentBtn">حفظ الموعد</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarScreen;
